import {Group, MaterialInput, StoredMesh, TemplateInstance, TemplateReference, InputNode, TemplateNode} from "@cm/template-nodes"
import type {MaterialAssignmentConstructorParameters, TemplateNodes, TemplateGraph} from "@cm/template-nodes"
import {Mattress} from "#mattress/nodes/mattress"

function toKebab(value: string): string {
    const cleaned = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    return cleaned.length > 0 ? cleaned : "material"
}

function uniqueId(base: string, used: Set<string>): string {
    if (!used.has(base)) {
        used.add(base)
        return base
    }
    let i = 2
    while (used.has(`${base}-${i}`)) i++
    const id = `${base}-${i}`
    used.add(id)
    return id
}

function collectExistingInputIds(templateGraph: TemplateGraph): Set<string> {
    const ids = new Set<string>()
    const walk = (nodesNode: TemplateNodes): void => {
        for (const item of nodesNode.parameters.list) {
            if (!(item instanceof TemplateNode)) continue
            if (item instanceof Mattress || item instanceof TemplateInstance || item instanceof TemplateReference) continue
            if (item instanceof InputNode) {
                ids.add(item.parameters.id)
                continue
            }
            if (item instanceof Group) walk(item.parameters.nodes)
        }
    }
    walk(templateGraph.parameters.nodes)
    return ids
}

export function removeMaterialInputsForStoredMeshes(templateGraph: TemplateGraph): void {
    const inputsToRemove = new Set<MaterialInput>()

    const visitForUnhook = (nodesNode: TemplateNodes): void => {
        for (const item of nodesNode.parameters.list) {
            if (!(item instanceof TemplateNode)) continue
            if (item instanceof Group) {
                visitForUnhook(item.parameters.nodes)
                continue
            }
            if (!(item instanceof StoredMesh)) continue
            const matAssignments = item.parameters.materialAssignments
            for (const [slotKey, oldAssignment] of Object.entries(matAssignments.parameters)) {
                if (!oldAssignment || !(oldAssignment.parameters.node instanceof MaterialInput)) continue
                const input = oldAssignment.parameters.node
                const defaultValue = input.parameters.default ?? null
                item.assignMaterial(slotKey, {
                    node: defaultValue,
                    side: oldAssignment.parameters.side,
                    horizontalOffset: oldAssignment.parameters.horizontalOffset,
                    verticalOffset: oldAssignment.parameters.verticalOffset,
                    rotation: oldAssignment.parameters.rotation,
                    alphaHashing: oldAssignment.parameters.alphaHashing,
                })
                oldAssignment.updateParameters({node: null})
                inputsToRemove.add(input)
            }
        }
    }
    visitForUnhook(templateGraph.parameters.nodes)

    if (inputsToRemove.size === 0) return

    const visitForRemoval = (nodesNode: TemplateNodes): void => {
        const originalList = nodesNode.parameters.list
        let mutated = false
        const newList = originalList.filter((item) => {
            if (item instanceof MaterialInput && inputsToRemove.has(item)) {
                if (item.parents.size > 1) return true
                mutated = true
                return false
            }
            return true
        })
        if (mutated) nodesNode.updateParameters({list: newList})
        for (const item of newList) {
            if (!(item instanceof TemplateNode)) continue
            if (item instanceof Mattress || item instanceof TemplateInstance || item instanceof TemplateReference) continue
            if (item instanceof Group) visitForRemoval(item.parameters.nodes)
        }
    }
    visitForRemoval(templateGraph.parameters.nodes)
}

export function insertMaterialInputsForStoredMeshes(templateGraph: TemplateGraph): void {
    const usedIds = collectExistingInputIds(templateGraph)

    const walk = (nodesNode: TemplateNodes): void => {
        const originalList = nodesNode.parameters.list
        const newList: typeof originalList = []
        let mutated = false

        for (const item of originalList) {
            newList.push(item)

            if (!(item instanceof TemplateNode)) continue

            if (item instanceof Group) {
                walk(item.parameters.nodes)
                continue
            }

            if (!(item instanceof StoredMesh)) continue

            const slots = item.getMaterialSlots()
            const slotDisplayNames = item.getMaterialSlotNames() ?? {}
            const slotsToFill: Array<{slotKey: string; existing: MaterialAssignmentConstructorParameters | null}> = []
            for (const [slotKey, assignment] of Object.entries(slots)) {
                if (assignment && assignment.node instanceof MaterialInput) continue
                slotsToFill.push({slotKey, existing: assignment})
            }
            if (slotsToFill.length === 0) continue

            const meshName = item.parameters.name ?? "Mesh"
            const multiple = slotsToFill.length > 1

            for (const {slotKey, existing} of slotsToFill) {
                const slotDisplay = slotDisplayNames[slotKey] ?? slotKey
                const name = multiple ? `${meshName} - ${slotDisplay}` : meshName
                const baseId = multiple ? `${toKebab(meshName)}-${toKebab(slotDisplay)}` : toKebab(meshName)
                const id = uniqueId(baseId, usedIds)
                const newInput = new MaterialInput({
                    parameters: {
                        id,
                        name,
                        default: existing?.node ?? undefined,
                    },
                })
                const newAssignment: MaterialAssignmentConstructorParameters = {
                    node: newInput,
                    side: existing?.side ?? "front",
                    horizontalOffset: existing?.horizontalOffset,
                    verticalOffset: existing?.verticalOffset,
                    rotation: existing?.rotation,
                    alphaHashing: existing?.alphaHashing,
                }
                item.assignMaterial(slotKey, newAssignment)
                newList.push(newInput)
                mutated = true
            }
        }

        if (mutated) nodesNode.updateParameters({list: newList})
    }

    walk(templateGraph.parameters.nodes)
}
